/**
 * Hierarchical TanStack Query keys for the Deepfake Simulation module.
 *
 * Mode-less and workspace-less, as ESA's and Smishing's are: NO `mode`, NO
 * `workspaceId` (and no company or domain) in any key. Tenant isolation is
 * `WorkspaceCacheGuard` (queryClient.clear() on a workspace or company switch) plus
 * the `x-workspace-id` header `fetchApi` attaches. Keys carry resource + filters
 * ONLY; lib/deepfake/__tests__/query-keys.spec.ts holds that.
 *
 * Filters go INTO the key so distinct filter combinations cache apart, and a
 * namespace's `all()` is the prefix a targeted `invalidateQueries` refreshes
 * (never a global refetch). `lists()` is the prefix of every list of a namespace
 * and, unlike `all()`, does not match an open detail query.
 */

/** A filter object folded into a key; structural, so a module's typed filter interface passes uncast. */
type Filters = object | undefined

export const deepfakeKeys = {
  all: () => ['deepfake'] as const,

  campaigns: {
    all: () => [...deepfakeKeys.all(), 'campaigns'] as const,
    lists: () => [...deepfakeKeys.campaigns.all(), 'list'] as const,
    list: (filters?: Filters) => [...deepfakeKeys.campaigns.lists(), filters ?? {}] as const,
    detail: (id: string) => [...deepfakeKeys.campaigns.all(), 'detail', id] as const,
    /** One campaign's people (the funnel table). */
    targets: (id: string, filters?: Filters) =>
      [...deepfakeKeys.campaigns.all(), 'targets', id, filters ?? {}] as const,
  },

  /** The uploaded awareness videos (the media library). */
  videos: {
    all: () => [...deepfakeKeys.all(), 'videos'] as const,
    lists: () => [...deepfakeKeys.videos.all(), 'list'] as const,
    list: (filters?: Filters) => [...deepfakeKeys.videos.lists(), filters ?? {}] as const,
    detail: (id: string) => [...deepfakeKeys.videos.all(), 'detail', id] as const,
  },

  scenarios: {
    all: () => [...deepfakeKeys.all(), 'scenarios'] as const,
    lists: () => [...deepfakeKeys.scenarios.all(), 'list'] as const,
    list: (filters?: Filters) => [...deepfakeKeys.scenarios.lists(), filters ?? {}] as const,
    detail: (id: string) => [...deepfakeKeys.scenarios.all(), 'detail', id] as const,
  },

  /** The look-alike meeting page of a campaign (one per campaign). */
  meetingPages: {
    all: () => [...deepfakeKeys.all(), 'meeting-pages'] as const,
    lists: () => [...deepfakeKeys.meetingPages.all(), 'list'] as const,
    list: (filters?: Filters) => [...deepfakeKeys.meetingPages.lists(), filters ?? {}] as const,
    detail: (campaignId: string) => [...deepfakeKeys.meetingPages.all(), 'detail', campaignId] as const,
  },

  /** The cross-campaign reply inbox and its threads (replies are read in the product). */
  replies: {
    all: () => [...deepfakeKeys.all(), 'replies'] as const,
    lists: () => [...deepfakeKeys.replies.all(), 'list'] as const,
    list: (filters?: Filters) => [...deepfakeKeys.replies.lists(), filters ?? {}] as const,
    thread: (id: string) => [...deepfakeKeys.replies.all(), 'thread', id] as const,
  },

  /** SQL-aggregated reports; `name` is the report (summary, departments, ...), filters its window. */
  reports: {
    all: () => [...deepfakeKeys.all(), 'reports'] as const,
    report: (name: string, filters?: Filters) =>
      [...deepfakeKeys.reports.all(), name, filters ?? {}] as const,
  },

  settings: {
    all: () => [...deepfakeKeys.all(), 'settings'] as const,
    effective: () => [...deepfakeKeys.settings.all(), 'effective'] as const,
    raw: (scope: string) => [...deepfakeKeys.settings.all(), 'raw', scope] as const,
  },

  /** The ready sending domains the launch picker offers. */
  senderDomains: {
    all: () => [...deepfakeKeys.all(), 'sender-domains'] as const,
  },
} as const
